import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  TrendingUp, TrendingDown, Activity, Users, Target, Award,
  FileText, BarChart3, Calendar, RefreshCw, Loader2, ChevronRight,
  Heart, Thermometer, Smile, MessageSquare, Shield, Clock,
  ArrowUp, ArrowDown, Minus, Download, AlertTriangle
} from "lucide-react";

const getTrendIcon = (trend) => {
  switch (trend) {
    case "improving":
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    case "declining":
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    default:
      return <Minus className="w-4 h-4 text-slate-400" />;
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return "text-green-500";
  if (score >= 60) return "text-yellow-500";
  return "text-red-500";
};

const getScoreBgColor = (score) => {
  if (score >= 80) return "bg-green-500";
  if (score >= 60) return "bg-yellow-500";
  return "bg-red-500";
};

const outcomeTypeLabels = {
  pain_management: { label: "Pain Management", icon: Thermometer, color: "text-red-500" },
  symptom_control: { label: "Symptom Control", icon: Activity, color: "text-orange-500" },
  quality_of_life: { label: "Quality of Life", icon: Heart, color: "text-pink-500" },
  family_satisfaction: { label: "Family Satisfaction", icon: Smile, color: "text-blue-500" },
  care_coordination: { label: "Care Coordination", icon: MessageSquare, color: "text-purple-500" }
};

export default function PatientOutcomesDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("30d");
  const [dashboard, setDashboard] = useState(null);
  const [regulatoryReport, setRegulatoryReport] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get(`/outcomes/dashboard?period=${period}`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch outcomes dashboard:", error);
      toast.error("Failed to load outcomes dashboard");
    }
  }, [period]);

  const fetchRegulatoryReport = useCallback(async () => {
    try {
      const response = await api.get(`/outcomes/reports/regulatory?period=${period}`);
      setRegulatoryReport(response.data);
    } catch (error) {
      console.error("Failed to fetch regulatory report:", error);
    }
  }, [period]);

  const fetchTrends = useCallback(async () => {
    try {
      const response = await api.get("/outcomes/reports/trends?months=6");
      setTrendsData(response.data);
    } catch (error) {
      console.error("Failed to fetch trends:", error);
    }
  }, []);

  const seedDemoData = async () => {
    try {
      const response = await api.post("/outcomes/seed-demo-data");
      if (response.data.success) {
        toast.success(`Created ${response.data.outcomes_created} outcomes and ${response.data.goals_created} goals`);
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Failed to seed demo data");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchRegulatoryReport(), fetchTrends()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchRegulatoryReport, fetchTrends]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboard(), fetchRegulatoryReport(), fetchTrends()]);
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  // Check if user has leadership access
  const allowedRoles = ["admin", "administrator", "leadership", "director", "manager"];
  if (!allowedRoles.includes(user?.role?.toLowerCase())) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800 border-slate-700 max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Restricted</h2>
            <p className="text-slate-400 mb-4">
              The Patient Outcomes Dashboard is available to leadership roles only.
            </p>
            <Button onClick={() => navigate(-1)} variant="outline" className="border-slate-600">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const byType = dashboard?.by_outcome_type || {};
  const goals = dashboard?.goals || [];
  const qualityMetrics = dashboard?.quality_metrics || {};

  return (
    <div className="min-h-screen bg-slate-900" data-testid="patient-outcomes-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-teal-500/20 rounded-lg">
                <BarChart3 className="w-8 h-8 text-teal-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Patient Outcomes Dashboard</h1>
                <p className="text-slate-400 text-sm">
                  Leadership view • Quality metrics & regulatory compliance
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32 bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="1y">Last year</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="border-slate-600 text-slate-300"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button variant="outline" className="border-slate-600 text-slate-300" onClick={seedDemoData}>
                Seed Demo Data
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Overall Quality Score</p>
                  <p className={`text-3xl font-bold ${getScoreColor(summary.overall_quality_score)}`}>
                    {summary.overall_quality_score || 0}%
                  </p>
                </div>
                <div className="p-3 bg-teal-500/20 rounded-full">
                  <Award className="w-6 h-6 text-teal-400" />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                {getTrendIcon(summary.quality_trend)}
                <span className={`text-sm ${summary.quality_change >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {summary.quality_change >= 0 ? "+" : ""}{summary.quality_change || 0}% vs prev period
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Patients</p>
                  <p className="text-3xl font-bold text-white">{summary.total_patients || 0}</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-full">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <p className="text-slate-500 text-sm mt-2">
                {summary.outcomes_recorded || 0} outcomes recorded
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Goals Achieved</p>
                  <p className="text-3xl font-bold text-green-400">
                    {goals.filter(g => g.status === "achieved").length}/{goals.length}
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-full">
                  <Target className="w-6 h-6 text-green-400" />
                </div>
              </div>
              <Progress 
                value={goals.length ? (goals.filter(g => g.status === "achieved").length / goals.length * 100) : 0} 
                className="mt-2 bg-slate-700"
              />
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Compliance Status</p>
                  <p className={`text-xl font-bold ${regulatoryReport?.compliance_status?.overall === "compliant" ? "text-green-400" : "text-yellow-400"}`}>
                    {regulatoryReport?.compliance_status?.overall === "compliant" ? "Compliant" : "At Risk"}
                  </p>
                </div>
                <div className={`p-3 ${regulatoryReport?.compliance_status?.overall === "compliant" ? "bg-green-500/20" : "bg-yellow-500/20"} rounded-full`}>
                  <Shield className={`w-6 h-6 ${regulatoryReport?.compliance_status?.overall === "compliant" ? "text-green-400" : "text-yellow-400"}`} />
                </div>
              </div>
              <p className="text-slate-500 text-sm mt-2">
                {regulatoryReport?.quality_indicators?.pain_assessment_compliance?.compliance_rate || 0}% pain assessment
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600">Overview</TabsTrigger>
            <TabsTrigger value="outcomes" className="data-[state=active]:bg-teal-600">By Outcome Type</TabsTrigger>
            <TabsTrigger value="goals" className="data-[state=active]:bg-teal-600">Goals</TabsTrigger>
            <TabsTrigger value="regulatory" className="data-[state=active]:bg-teal-600">Regulatory</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Quality Metrics */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Quality Metrics</CardTitle>
                  <CardDescription className="text-slate-400">Current scores by category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(outcomeTypeLabels).map(([key, { label, icon: Icon, color }]) => {
                      const score = qualityMetrics[key] || 0;
                      return (
                        <div key={key} className="flex items-center gap-4">
                          <Icon className={`w-5 h-5 ${color}`} />
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-slate-300">{label}</span>
                              <span className={`text-sm font-medium ${getScoreColor(score)}`}>{score}%</span>
                            </div>
                            <Progress value={score} className="h-2 bg-slate-700" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Outcomes */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Recent Outcomes</CardTitle>
                  <CardDescription className="text-slate-400">Latest recorded outcomes</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {(dashboard?.recent_outcomes || []).length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No outcomes recorded yet</p>
                        <Button variant="link" className="text-teal-400" onClick={seedDemoData}>
                          Seed demo data
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(dashboard?.recent_outcomes || []).slice(0, 8).map((outcome, index) => (
                          <div key={outcome.id || index} className="p-3 bg-slate-700/50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white font-medium">{outcome.patient_name || "Unknown"}</p>
                                <p className="text-slate-400 text-sm">
                                  {outcomeTypeLabels[outcome.outcome_type]?.label || outcome.outcome_type}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {getTrendIcon(outcome.trend)}
                                <span className={`font-bold ${getScoreColor(outcome.score)}`}>
                                  {outcome.score}%
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="outcomes" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(byType).map(([type, data]) => {
                const { label, icon: Icon, color } = outcomeTypeLabels[type] || { label: type, icon: Activity, color: "text-slate-400" };
                return (
                  <Card key={type} className="bg-slate-800 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-2 bg-slate-700 rounded-lg`}>
                          <Icon className={`w-5 h-5 ${color}`} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{label}</p>
                          <p className="text-slate-400 text-sm">{data.count} outcomes</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-sm">Average Score</span>
                        <span className={`text-lg font-bold ${getScoreColor(data.avg_score)}`}>
                          {data.avg_score}%
                        </span>
                      </div>
                      <Progress value={data.avg_score} className="h-2 bg-slate-700 mb-3" />
                      <div className="flex justify-between text-sm">
                        <span className="text-green-400 flex items-center gap-1">
                          <ArrowUp className="w-3 h-3" /> {data.improving} improving
                        </span>
                        <span className="text-red-400 flex items-center gap-1">
                          <ArrowDown className="w-3 h-3" /> {data.declining} declining
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="goals" className="mt-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Outcome Goals</CardTitle>
                <CardDescription className="text-slate-400">Track progress towards organizational goals</CardDescription>
              </CardHeader>
              <CardContent>
                {goals.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No goals set yet</p>
                    <Button variant="link" className="text-teal-400" onClick={seedDemoData}>
                      Seed demo goals
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {goals.map((goal, index) => (
                      <div key={goal.id || index} className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-white font-medium">{goal.description}</p>
                            <p className="text-slate-400 text-sm">
                              {outcomeTypeLabels[goal.outcome_type]?.label || goal.outcome_type} • {goal.period}
                            </p>
                          </div>
                          <Badge className={goal.status === "achieved" ? "bg-green-500" : "bg-blue-500"}>
                            {goal.status === "achieved" ? "Achieved" : "In Progress"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-400">Current: {goal.current_avg || 0}%</span>
                              <span className="text-slate-400">Target: {goal.target_score}%</span>
                            </div>
                            <Progress value={goal.progress_percent || 0} className="h-2 bg-slate-700" />
                          </div>
                          <span className={`text-lg font-bold ${goal.progress_percent >= 100 ? "text-green-400" : "text-blue-400"}`}>
                            {goal.progress_percent || 0}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="regulatory" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-teal-400" />
                    Regulatory Compliance Report
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Generated {regulatoryReport?.generated_at ? new Date(regulatoryReport.generated_at).toLocaleDateString() : "N/A"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-slate-400 text-sm">Census</p>
                      <p className="text-white font-medium">
                        {regulatoryReport?.census?.active_patients || 0} active patients
                      </p>
                    </div>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-slate-400 text-sm">Pain Assessment Compliance</p>
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium">
                          {regulatoryReport?.quality_indicators?.pain_assessment_compliance?.compliance_rate || 0}%
                        </p>
                        <Badge className={
                          (regulatoryReport?.quality_indicators?.pain_assessment_compliance?.compliance_rate || 0) >= 95
                            ? "bg-green-500" : "bg-yellow-500"
                        }>
                          Target: 95%
                        </Badge>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-slate-400 text-sm">Visit Frequency</p>
                      <p className="text-white font-medium">
                        {regulatoryReport?.quality_indicators?.visit_frequency?.avg_per_patient || 0} visits/patient
                      </p>
                    </div>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-slate-400 text-sm">Patient Engagement</p>
                      <p className="text-white font-medium">
                        {regulatoryReport?.quality_indicators?.patient_engagement?.engagement_rate || 0}% engagement rate
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-400" />
                    Compliance Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`p-4 rounded-lg mb-4 ${
                    regulatoryReport?.compliance_status?.overall === "compliant" 
                      ? "bg-green-500/20 border border-green-500/50" 
                      : "bg-yellow-500/20 border border-yellow-500/50"
                  }`}>
                    <div className="flex items-center gap-3">
                      {regulatoryReport?.compliance_status?.overall === "compliant" ? (
                        <Shield className="w-8 h-8 text-green-400" />
                      ) : (
                        <AlertTriangle className="w-8 h-8 text-yellow-400" />
                      )}
                      <div>
                        <p className={`text-lg font-bold ${
                          regulatoryReport?.compliance_status?.overall === "compliant" ? "text-green-400" : "text-yellow-400"
                        }`}>
                          {regulatoryReport?.compliance_status?.overall === "compliant" ? "Fully Compliant" : "At Risk"}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {regulatoryReport?.compliance_status?.overall === "compliant" 
                            ? "All regulatory requirements met" 
                            : "Some metrics below target"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-400 text-sm">Coverage Rate</p>
                    <Progress 
                      value={regulatoryReport?.outcome_summary?.coverage_rate || 0} 
                      className="h-3 bg-slate-700" 
                    />
                    <p className="text-white text-right">
                      {regulatoryReport?.outcome_summary?.coverage_rate || 0}%
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
